// ─── client-side voice activity detection ────────────────────────────────────
// `turnTaking: 'vad'` asks the client to declare turn boundaries (shared/session.ts),
// and `speech.start` doubles as the barge-in signal — so this is what makes talking
// over the assistant work. An RMS gate with a rise time and a hangover is enough:
// anything cleverer belongs in a model, not here.
//
// Pure and clock-injected, so the thresholds are testable without a microphone.
//
// `hangoverMs` is NOT this file's number to choose: it is how much silence the mic
// keeps sending, and a server-placed provider is counting that same silence to decide
// when to answer. It comes from `shared/session.ts::TURN_END`, which explains what
// happens when the two disagree.

import { TURN_END } from '@shared/session.ts'

export interface VadOptions {
  /** RMS above which a block counts as speech. ~0.02 is a quiet room's ceiling. */
  threshold?: number
  /** Speech must persist this long before a turn opens — kills coughs and clicks. */
  minSpeechMs?: number
  /** Silence must persist this long before a turn closes. Mid-sentence pauses are shorter. */
  hangoverMs?: number
}

export type VadEdge = 'start' | 'stop' | null

/** Block RMS at which sound counts as someone talking: about a quiet room's ceiling. */
export const SPEECH_RMS = 0.02

const DEFAULTS: Required<VadOptions> = { threshold: SPEECH_RMS, minSpeechMs: 120, hangoverMs: TURN_END.clientHangoverMs }

export class Vad {
  private readonly o: Required<VadOptions>
  private speaking = false
  /** When the current run of above/below-threshold blocks began. */
  private since = 0
  private loud = false

  constructor(options: VadOptions = {}) {
    this.o = { ...DEFAULTS, ...options }
  }

  get open(): boolean {
    return this.speaking
  }

  /** Feed one block's RMS. Returns the turn edge it crossed, if any. */
  push(rms: number, nowMs: number): VadEdge {
    const loud = rms >= this.o.threshold
    if (loud !== this.loud) {
      this.loud = loud
      this.since = nowMs
    }
    const held = nowMs - this.since

    if (!this.speaking && loud && held >= this.o.minSpeechMs) {
      this.speaking = true
      return 'start'
    }
    if (this.speaking && !loud && held >= this.o.hangoverMs) {
      this.speaking = false
      return 'stop'
    }
    return null
  }

  /** Force the turn closed, e.g. when the mic is muted or the session ends. */
  reset(): VadEdge {
    const wasSpeaking = this.speaking
    this.speaking = false
    this.loud = false
    this.since = 0
    return wasSpeaking ? 'stop' : null
  }
}
